/**
 * Authorization Code Flow Service
 * Implements proper OAuth 2.0 Authorization Code flow for user authentication
 */

import { AUTHENTICATION_URL } from './api-config';
import { setAuthToken, getAuthToken } from '../utils/auth-utils';

/**
 * Check if the authentication backend is accessible
 */
export const checkAuthBackendHealth = async () => {
  // Cached result (60s)
  if (typeof window !== 'undefined' && window.__authHealthCache && Date.now() - window.__authHealthCache.timestamp < 60000) {
    return window.__authHealthCache.result;
  }
  try {
    if (!AUTHENTICATION_URL) {
      const result = { healthy: false, error: 'AUTHENTICATION_URL not configured' };
      if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
      return result;
    }
    const healthUrl = `${AUTHENTICATION_URL}/healthcheck`;
    try {
      const r = await fetch(healthUrl, { method: 'GET' });
      if (r.ok) {
        const result = { healthy: true, message: 'healthcheck OK' };
        if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
        return result;
      }
      if (r.status === 404) {
        // Fallback to base probe
        const base = await fetch(AUTHENTICATION_URL, { method: 'GET' });
        const result = base.ok || base.status === 404
          ? { healthy: true, message: 'Backend reachable (no health endpoint)' }
          : { healthy: false, error: `Base probe status: ${base.status}` };
        if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
        return result;
      }
      const result = { healthy: false, error: `Healthcheck status: ${r.status}` };
      if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
      return result;
    } catch (e) {
      // Network error -> try base
      try {
        const base = await fetch(AUTHENTICATION_URL, { method: 'GET' });
        const result = base.ok || base.status === 404
          ? { healthy: true, message: 'Backend reachable (no health endpoint)' }
          : { healthy: false, error: `Base probe status: ${base.status}` };
        if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
        return result;
      } catch (be) {
        const result = { healthy: false, error: `Backend unreachable: ${be.message}` };
        if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
        return result;
      }
    }
  } catch (err) {
    const result = { healthy: false, error: err.message };
    if (typeof window !== 'undefined') window.__authHealthCache = { timestamp: Date.now(), result };
    return result;
  }
};

// State helpers
const generateState = () => Math.random().toString(36).substring(2,15) + Math.random().toString(36).substring(2,15);
const storeState = (state) => sessionStorage.setItem('oauth_state', state);
const verifyState = (received) => {
  const stored = sessionStorage.getItem('oauth_state');
  if (stored === received) {
    sessionStorage.removeItem('oauth_state');
    return true;
  }
  return false;
};
export const getAuthorizationUrl = async () => {
  try {
    if (!AUTHENTICATION_URL) {
      throw new Error('AUTHENTICATION_URL is not configured.');
    }
    const endpoint = `${AUTHENTICATION_URL}/oauth/auth-url`;
    const response = await fetch(endpoint, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to get authorization URL: ${response.status} - ${text}`);
    }
    const data = await response.json();
    if (data.status !== 'success') {
      throw new Error(data.message || 'Failed to get authorization URL');
    }
    storeState(data.state);
    return { authUrl: data.auth_url, state: data.state, redirectUri: data.redirect_uri };
  } catch (e) {
    console.error('Error getting authorization URL:', e);
    throw e;
  }
};

export const verifyEmailAuthorization = async (email) => {
  if (!AUTHENTICATION_URL) {
    console.error('verifyEmailAuthorization: AUTHENTICATION_URL is not configured.');
    return { authorized: false, message: 'Authentication service is not configured. Please contact support.' };
  }
  try {
    const endpoint = `${AUTHENTICATION_URL}/auth/email-verify`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    return {
      authorized: response.ok && data.authorized === true,
      message: data.message || 'You are not authorized currently to access the application',
    };
  } catch (e) {
    console.error('Error verifying email authorization:', e);
    return { authorized: false, message: 'Unable to verify your details. Please try again.' };
  }
};

// Initiate login: fetch auth URL then redirect
export const initiateAuthorizationCodeLogin = async () => {
  try {
    const { authUrl, redirectUri } = await getAuthorizationUrl();
    if (!authUrl) throw new Error('No authorization URL received from backend');
    console.log('[OAuth] Navigating to backend-provided authUrl:', authUrl);
    window.location.href = authUrl;
  } catch (e) {
    console.error('Error initiating authorization code login:', e);
    throw e;
  }
};

/**
 * Handle authorization callback and exchange code for tokens
 * Step 3 of OAuth Authorization Code flow
 * @param {string} code - Authorization code from URL parameters
 * @param {string} state - State parameter from URL parameters
 * @param {string} error - Error parameter from URL parameters
 * @returns {Promise<object>} Authentication result with user info
 */
export const handleAuthorizationCallback = async (code, state, error) => {
  try {
    console.log('Handling authorization callback...');
    console.log('Current URL:', window.location.href);
    console.log('Code received:', code ? 'Yes' : 'No');
    console.log('State received:', state || 'None');
    console.log('Error received:', error || 'None');

    // Check for error parameter
    if (error) {
      throw new Error(`Authorization error: ${error}`);
    }

    // Validate required parameters
    if (!code) {
      throw new Error('No authorization code received');
    }

    // Verify state parameter for security
    // Note: Make this check more flexible to handle backend-generated states
    if (state) {
      const isStateValid = verifyState(state);
      if (!isStateValid) {
        console.warn('State parameter mismatch detected. This could be due to:');
        console.warn('1. Backend-generated state vs frontend-stored state');
        console.warn('2. Page refresh during authentication flow');
        console.warn('3. Multiple authentication attempts');
        console.warn('Continuing with authentication process...');
        // Clear any existing oauth state to prevent future conflicts
        sessionStorage.removeItem('oauth_state');
      } else {
        console.log('State parameter verified successfully');
      }
    } else {
      console.log('No state parameter received - this may be expected in some configurations');
    }

    console.log('Authorization code received, exchanging for access token...');

    // Exchange authorization code for access token using backend endpoint
  const tokenEndpoint = `${AUTHENTICATION_URL}/oauth/user-token?authorization_code=${encodeURIComponent(code)}`;
    
    let result; // Declare result variable in broader scope
    
    try {
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Token exchange response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Token exchange failed - Response body:', errorText);
        
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText, rawError: errorText };
        }
        
        console.error('Token exchange failed - Parsed error:', errorData);
        
        // Provide more specific error messages based on status code
        let errorMessage = errorData.message || `Token exchange failed: ${response.status}`;
        if (response.status === 404) {
          errorMessage = 'Authentication service endpoint not found. Please check if the auth service is running.';
        } else if (response.status === 500) {
          errorMessage = 'Authentication service internal error. Please check the backend logs.';
        } else if (response.status === 400) {
          errorMessage = 'Invalid authorization code or request format.';
        } else if (response.status === 0 || !response.status) {
          errorMessage = 'Unable to connect to authentication service. Please check if it\'s running.';
        }
        
        throw new Error(errorMessage);
      }

      result = await response.json(); // Assign to the declared variable
      console.log('Token exchange result status:', result.status);
      
      if (result.status !== 'success') {
        console.error('Token exchange failed - Backend returned error:', result);
        throw new Error(result.message || 'Token exchange failed');
      }

      console.log('Access token received successfully');
    } catch (fetchError) {
      console.error('Network error during token exchange:', fetchError);
      
      // Handle network-level errors
      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        throw new Error('Unable to connect to authentication service. Please ensure the authentication backend is running at ' + AUTHENTICATION_URL);
      }
      
      // Re-throw other errors
      throw fetchError;
    }

    // Ensure result was successfully obtained before proceeding
    if (!result) {
      throw new Error('Token exchange failed - no result received');
    }

    // Store the API access token — used for AFR API bearer auth.
    if (result.token_info && result.token_info.access_token) {
      setAuthToken(result.token_info.access_token);
      console.log('Access token stored in session');
    }

    // Store the Graph access token separately — used only for Microsoft Graph calls (photo etc).
    if (result.token_info && result.token_info.graph_access_token) {
      sessionStorage.setItem('graphToken', result.token_info.graph_access_token);
    }

    // Transform user data to our standard format
    const userInfo = transformUserData(result.user_data);

    // Store user info in session first (so auth works even if photo fails)
    sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
    console.log('User info stored in session:', userInfo?.name);

    // Try to fetch user photo asynchronously — use Graph token, not API token.
    const graphToken = sessionStorage.getItem('graphToken');
    if (graphToken) {
      // Fetch photo in background without blocking auth completion
      fetchUserPhoto(graphToken).then(photo => {
        if (photo) {
          const updatedUserInfo = { ...userInfo, photo };
          sessionStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
          console.log('User photo fetched and stored successfully');
          // Trigger an event to update components
          window.dispatchEvent(new Event('userPhotoUpdated'));
        } else {
          console.log('No user photo available');
        }
      }).catch(photoError => {
        console.warn('Could not fetch user photo:', photoError);
      });
    }
    console.log('User info stored in session:', userInfo?.name);

    // Trigger auth state change event to notify all components
    console.log('Triggering auth state change event...');
    window.dispatchEvent(new Event('authStateChanged'));

    console.log('User authentication completed successfully');

    // Clean up URL to avoid reusing the same authorization code on refresh/navigation
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      url.searchParams.delete('state');
      url.searchParams.delete('session_state');
      const newUrl = url.pathname + (url.search ? '?' + url.searchParams.toString() : '') + url.hash;
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) {
      console.warn('Could not clean auth query params from URL:', e);
    }

    return {
      success: true,
      userInfo: userInfo,
      tokenInfo: result.token_info,
      message: 'Authentication successful'
    };

  } catch (error) {
    console.error('Error handling authorization callback:', error);

    // Prevent retry loops with stale code/state and stale session.
    setAuthToken(null);
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('justAuthenticated');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('graphToken');

    try {
      const cleanUrl = `${window.location.origin}${window.location.pathname}${window.location.hash || ''}`;
      window.history.replaceState({}, document.title, cleanUrl);
    } catch (cleanupError) {
      console.warn('Failed to clean callback URL after auth error:', cleanupError);
    }

    throw error;
  }
};

/**
 * Transform user data from backend to our standard format
 * @param {object} userData - User data from backend
 * @returns {object} Standardized user info
 */
const transformUserData = (userData) => {
  if (!userData) {
    return null;
  }

  return {
    id: userData.id || '',
    name: userData.displayName || userData.givenName || 'User',
    email: userData.email || userData.mail || userData.userPrincipalName || '',
    initials: getInitials(userData.displayName || userData.givenName || 'User'),
    fullName: userData.displayName || `${userData.givenName || ''} ${userData.surname || ''}`.trim() || 'User',
    givenName: userData.givenName || '',
    familyName: userData.surname || '',
    jobTitle: userData.jobTitle || '',
    department: userData.department || '',
    userPrincipalName: userData.userPrincipalName || '',
    officeLocation: userData.officeLocation || '',
    mobilePhone: userData.mobilePhone || '',
    businessPhones: userData.businessPhones || [],
    photo: null, // Will be fetched separately if needed
  };
};

/**
 * Get user initials from full name
 * @param {string} name - Full name
 * @returns {string} User initials
 */
const getInitials = (name) => {
  if (!name) return 'U';
  
  const nameParts = name.trim().split(' ');
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  
  return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
};

/**
 * Fetch user photo from Microsoft Graph API
 * @param {string} accessToken - Access token
 * @returns {Promise<string|null>} Base64 encoded photo URL or null
 */
export const fetchUserPhoto = async (accessToken) => {
  try {
    console.log('Fetching user photo from Microsoft Graph API...');
    
    if (!accessToken) {
      console.log('No access token provided for photo fetch');
      return null;
    }
    
    const response = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'image/*'
      }
    });
    
    console.log('Photo response status:', response.status);
    
    if (response.ok) {
      const blob = await response.blob();

      // Convert blob to base64 data URL so it can be stored in sessionStorage and
      // survive page reloads. Object URLs created by URL.createObjectURL are
      // not persistent across page reloads which causes the avatar to disappear.
      const dataUrl = await new Promise((resolve, reject) => {
        try {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(blob);
        } catch (err) {
          reject(err);
        }
      });
      console.log('User photo fetched successfully, data URL length:', dataUrl ? dataUrl.length : 0);
      return dataUrl;
    } else if (response.status === 404) {
      console.log('User has no profile photo (404)');
      return null;
    } else {
      console.log('Failed to fetch user photo, status:', response.status);
      const errorText = await response.text().catch(() => 'Unable to read error response');
      console.log('Photo fetch error response:', errorText);
      return null;
    }
  } catch (error) {
    console.error("Error fetching user photo:", error);
    return null;
  }
};

/**
 * Get current user info using stored access token
 * @returns {Promise<object|null>} User information
 */
export const getUserInfoWithToken = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      console.log('No auth token available for user info request');
      return null;
    }

    console.log('Retrieving user information with token...');

    const response = await fetch(`${AUTHENTICATION_URL}/user-info-with-token`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('User info response status:', response.status);

    if (!response.ok) {
      console.log('Failed to get user info, token may be expired or invalid');
      // If the token is invalid, clear it
      if (response.status === 401) {
        console.log('Token appears to be expired or invalid, clearing storage');
        setAuthToken(null);
        sessionStorage.removeItem('userInfo');
      }
      return null;
    }

    const result = await response.json();
    console.log('User info response result:', result);
    
    if (result.status === 'success' && result.user_data) {
      const userInfo = transformUserData(result.user_data);
      
      // Try to fetch user photo — use Graph token if available, fall back silently.
      try {
        const graphToken = sessionStorage.getItem('graphToken');
        const photo = graphToken ? await fetchUserPhoto(graphToken) : null;
        if (photo) {
          userInfo.photo = photo;
        }
      } catch (photoError) {
        console.log('Could not fetch user photo');
      }
      
      // Update stored user info
      sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      console.log('User information retrieved and stored successfully');
      return userInfo;
    }

    return null;
  } catch (error) {
    console.error('Error getting user info with token:', error);
    return null;
  }
};

/**
 * Check if we have valid authentication
 * @returns {Promise<object>} Authentication status
 */
export const checkAuthenticationStatus = async () => {
  try {
    console.log('Checking authentication status...');
    
    // Check if we have a stored token
    const token = getAuthToken();
    if (!token) {
      console.log('No stored token found');
      return { isAuthenticated: false, userInfo: null };
    }

    console.log('Stored token found, validating with backend...');

    // First try to get cached user info
    const cachedUserInfo = getCachedUserInfo();
    if (cachedUserInfo) {
      console.log('Cached user info found, validating token...');
      
      // Try to get user info with the token to validate it's still valid
      const userInfo = await getUserInfoWithToken();
      
      if (userInfo) {
        console.log('Token validation successful');
        return { isAuthenticated: true, userInfo };
      } else {
        console.log('Token validation failed, clearing cache');
        // Token is invalid or expired, clear storage
        setAuthToken(null);
        sessionStorage.removeItem('userInfo');
        return { isAuthenticated: false, userInfo: null };
      }
    } else {
      console.log('No cached user info, attempting to fetch...');
      
      // No cached user info, try to get it
      const userInfo = await getUserInfoWithToken();
      
      if (userInfo) {
        console.log('Successfully retrieved user info');
        return { isAuthenticated: true, userInfo };
      } else {
        console.log('Failed to retrieve user info');
        // Token is invalid or expired, clear storage
        setAuthToken(null);
        sessionStorage.removeItem('userInfo');
        return { isAuthenticated: false, userInfo: null };
      }
    }
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return { isAuthenticated: false, userInfo: null };
  }
};

/**
 * Sign out user and clear authentication data
 */
export const signOut = () => {
  try {
    console.log('Signing out user...');
    
    // Clear stored tokens and user info
    setAuthToken(null);
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('graphToken');
    
    // Redirect to home page
    window.location.href = '/';
  } catch (error) {
    console.error('Error during sign out:', error);
  }
};

/**
 * Get cached user info from session storage
 * @returns {object|null} Cached user information
 */
export const getCachedUserInfo = () => {
  try {
    const cachedUserInfo = sessionStorage.getItem('userInfo');
    if (cachedUserInfo) {
      return JSON.parse(cachedUserInfo);
    }
    return null;
  } catch (error) {
    console.error('Error getting cached user info:', error);
    return null;
  }
};

/**
 * Debug function to log current authentication state
 */
export const debugAuthState = () => {
  console.log('=== Authentication Debug Info ===');
  console.log('AUTHENTICATION_URL:', AUTHENTICATION_URL);
  console.log('Access Token:', getAuthToken() ? 'Present' : 'Missing');
  console.log('Cached User Info:', getCachedUserInfo());
  console.log('OAuth State:', sessionStorage.getItem('oauth_state'));
  console.log('Current URL:', window.location.href);
  console.log('URL Search Params:');
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.forEach((value, key) => {
    console.log(`  ${key}: ${value}`);
  });
  console.log('================================');
};
